import { useTranslation } from "react-i18next";
import { ErrorToaster } from "components/common/toast";
import { successToaster } from "components/common/toast";
import CustomTableProducts from "components/ReactTable/CustomTableProducts";
import TableFilter from "components/TableFilter/TableFilter";
import { GetSectionCategoriesAPI } from "network/categoryAPI";
import { GetProductsAPI } from "network/productAPI";
import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, CardTitle } from "reactstrap";
import handleError from "utils/handleError";
import ReactBSAlert from "react-bootstrap-sweetalert";
import { DeletedProductAPI } from "network/productAPI";
import { GetSectionSubCategoriesAPI } from "network/categoryAPI";
import AssignCategoryModal from "components/modal/AssignCategoryModal";
import { SetManualProductAvailableAPI } from "network/menuItems";

function SectionProductsTable({ sectionData }) {
  const { t } = useTranslation("products");
  const pageSize = 10;

  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [alert, setAlert] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [categories, setCategories] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [fetchTrigger, setFetchTrigger] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [uncheck, setUncheck] = useState(false);

  const resetState = () => {
    setProducts([]);
    setSelectedRowIds([]);
    setCurrentPage(1);
    setTotalPages(1);
    setSearch("");
    setFilter("");
    setCategories("");
    setSubCategory("");
  };
  useEffect(() => {
    resetState();
  }, [sectionData]);
  const loadCategoryOptions = async (
    search,
    prevOptions = [],
    additional = { page: 1 }
  ) => {
    const page = additional.page || 1;
    try {
      const response = await GetSectionCategoriesAPI(
        sectionData?.uuid,
        page,
        pageSize,
        search
      );

      if (response.success) {
        const newOptions = response.data.categories.map((category) => ({
          value: category.uuid,
          label: category.name,
        }));
        const allOption = { value: "", label: t("list.all") };

        return {
          options: page === 1 ? [allOption, ...newOptions] : [...newOptions],
          hasMore: response.data?.has_next,
          additional: { page: page + 1 },
        };
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }

    return {
      options: prevOptions,
      hasMore: false,
      additional: { page: page + 1 },
    };
  };

  const loadSubCategoryOptionsFilter = async (
    search,
    prevOptions = [],
    additional = { page: 1 }
  ) => {
    const page = additional.page || 1;

    try {
      const response = await GetSectionSubCategoriesAPI(
        sectionData?.uuid,
        page,
        pageSize,
        search,
        categories?.value
      );

      if (response.success) {
        const newOptions = response.data.sub_categories.map((category) => ({
          value: category?.uuid,
          label: category?.name,
        }));
        const allOption = { value: "", label: t("list.all") };

        return {
          options: page === 1 ? [allOption, ...newOptions] : [...newOptions],
          hasMore: response?.data?.has_next,
          additional: { page: page + 1 },
        };
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }

    return {
      options: prevOptions,
      hasMore: false,
      additional: { page: page + 1 },
    };
  };

  const navigate = useNavigate();

  // Il ristorante senza CassaInCloud nasconde il vino finito (e lo rimostra quando torna disponibile).
  const toggleAvailable = async (product) => {
    const next = product.is_available === false;
    try {
      const isWine = sectionData?.name?.toLowerCase() === "wine";
      await SetManualProductAvailableAPI(isWine, product.uuid, next);
      setProducts((list) =>
        list.map((p) => (p.uuid === product.uuid ? { ...p, is_available: next } : p))
      );
      successToaster(next ? t("list.backAgain") : t("list.hidden"));
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: t("list.colEan"),
        accessor: (row) => row.attributes?.ean,
        width: 100,
        Cell: ({ value }) => value || "-",
      },
      {
        Header: t("list.colName"),
        accessor: "name",
        width: 200,
        Cell: ({ value, row }) => (
          <span style={row.original.is_available === false ? { opacity: 0.55 } : undefined}>
            {value || "-"}
            {row.original.is_available === false && (
              <span className="badge badge-secondary ml-2">{t("list.badgeHidden")}</span>
            )}
          </span>
        ),
      },
      {
        Header: t("list.colSource"),
        accessor: "source",
        Cell: ({ value }) => (value ? t(`list.sources.${value}`, { defaultValue: value }) : "-"),
      },
      {
        Header: t("list.colPrice"),
        accessor: "price",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: t("list.colStock"),
        accessor: "stock",
        Cell: ({ value }) => value || "-",
      },

      {
        Header: t("list.colCategory"),
        accessor: "categories",
        Cell: ({ value }) =>
          value && value.length
            ? `${value
                .slice(0, 3)
                .map((item) => item.name)
                .join(" , ")}${value.length > 3 ? " ..." : ""}`
            : "-",
      },

      {
        Header: t("list.colSubCategory"),

        accessor: "sub_categories",
        Cell: ({ value }) =>
          value && value.length
            ? `${value
                .slice(0, 3)
                .map((item) => item.name)
                .join(" , ")}${value.length > 3 ? " ..." : ""}`
            : "-",
      },

      {
        Header: t("list.colActions"),
        accessor: "Action",
        width: 100,
        Cell: ({ row }) => (
          <div className="d-flex  align-items-center">
            <i
              className="mr-3 pointer"
              onClick={() =>
                navigate(
                  `/${sectionData?.name?.toLowerCase()}/product/view/${
                    row?.original?.uuid
                  }`
                )
              }
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 576 512"
                width={20}
                height={20}
                className="eyeIcon"
              >
                <path d="M288 144a110.9 110.9 0 0 0 -31.2 5 55.4 55.4 0 0 1 7.2 27 56 56 0 0 1 -56 56 55.4 55.4 0 0 1 -27-7.2A111.7 111.7 0 1 0 288 144zm284.5 97.4C518.3 135.6 410.9 64 288 64S57.7 135.6 3.5 241.4a32.4 32.4 0 0 0 0 29.2C57.7 376.4 165.1 448 288 448s230.3-71.6 284.5-177.4a32.4 32.4 0 0 0 0-29.2zM288 400c-98.7 0-189.1-55-237.9-144C98.9 167 189.3 112 288 112s189.1 55 237.9 144C477.1 345 386.7 400 288 400z" />
              </svg>
            </i>
            <i
              className="tim-icons icon-pencil mr-3 text-warning pointer"
              onClick={() =>
                navigate(
                  `/${sectionData?.name?.toLowerCase()}/product/edit/${
                    row?.original?.uuid
                  }`
                )
              }
            ></i>
            {row?.original?.source === "manual" && (
              <i
                className={`fas fa-eye-slash mr-3 pointer ${
                  row.original.is_available === false ? "text-danger" : "text-muted"
                }`}
                title={
                  row.original.is_available === false
                    ? t("list.showAgainHint")
                    : t("list.hideHint")
                }
                onClick={() => toggleAvailable(row.original)}
              ></i>
            )}
            <i
              className="tim-icons icon-trash-simple text-danger pointer"
              onClick={() => warningWithConfirmMessage(row?.original?.uuid)}
            ></i>
          </div>
        ),
      },
    ],
    [sectionData, t]
  );
  const warningWithConfirmMessage = (id) => {
    setAlert(
      <ReactBSAlert
        warning
        style={{ display: "block", marginTop: "-100px" }}
        title={t("list.confirmTitle")}
        onConfirm={() => handleDelete(id)}
        onCancel={() => hideAlert()}
        confirmBtnBsStyle="success"
        cancelBtnBsStyle="danger"
        confirmBtnText={t("list.confirmYes")}
        cancelBtnText={t("list.cancel")}
        showCancel
        btnSize=""
      >
        {t("list.confirmDelete")}
      </ReactBSAlert>
    );
  };
  const hideAlert = () => {
    setAlert(null);
  };

  const handleDelete = async (id) => {
    try {
      const response = await DeletedProductAPI(sectionData?.uuid, id);
      if (response.success) {
        successToaster(response.message);
        setProducts((prevDataSet) =>
          prevDataSet.filter((value) => value.uuid !== id)
        );
        hideAlert();
      }
    } catch (error) {
      ErrorToaster(handleError(error?.status_code, error?.message));
      hideAlert();
    }
  };
  useEffect(() => {
    setCurrentPage(1);
    setFetchTrigger((prev) => !prev);
  }, [search, filter, categories, subCategory]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await GetProductsAPI(
          sectionData?.uuid,
          currentPage,
          pageSize,
          search,
          filter?.value,
          categories?.value,
          subCategory?.value
        );
        if (response.success) {
          setProducts(response?.data?.products);
          setTotalPages(response?.data?.total_pages);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [fetchTrigger, currentPage, sectionData?.uuid]);
  return (
    <div className="content">
      {alert}
      <Card className="p-4">
        <div className="d-flex gap-16 flex-wrap justify-content-between align-items-center mb-4">
          <CardTitle className="text-capitalize" tag="h2">
            {t("list.title", { name: sectionData?.name })}
          </CardTitle>
          <div className="d-flex flex-wrap">
            <Button
              className="mr-3"
              color="success"
              disabled={!selectedRowIds.length}
              onClick={() => {
                setModalOpen(true);
              }}
            >
              {t("list.assignCategory")}
            </Button>

            <Link to={`/${sectionData?.name?.toLowerCase()}/product/add`}>
              <Button className="text-capitalize" color="primary">
                {t("list.addProduct")}
              </Button>
            </Link>
          </div>
        </div>
        <TableFilter
          setSearch={setSearch}
          search={search}
          setFilter={setFilter}
          filter={filter}
          categories={categories}
          setCategories={setCategories}
          subCategory={subCategory}
          setSubCategory={setSubCategory}
          loadCategoryOptions={loadCategoryOptions}
          loadSubCategoryOptions={loadSubCategoryOptionsFilter}
          showSearch
          showCategory
          showSubCategory
          showFilter
        />

        <CustomTableProducts
          columns={columns}
          data={products || []}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          isLoading={isLoading}
          minWidth="1000px"
          selectedRowIds={selectedRowIds}
          setSelectedRowIds={setSelectedRowIds}
          setUncheck={setUncheck}
          uncheck={uncheck}
        />
      </Card>
      <AssignCategoryModal
        open={modalOpen}
        setOpen={setModalOpen}
        selectedRowIds={selectedRowIds}
        sectionId={sectionData?.uuid}
        setFetchTrigger={setFetchTrigger}
        setSelectedRowIds={setSelectedRowIds}
        setUncheck={setUncheck}
      />
    </div>
  );
}

export default SectionProductsTable;
